import { useMemo, useRef, useState } from "react";
import { useData } from "@/context/DataContext";
import AmmTabs from "@/components/AmmTabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Instagram, Facebook, Youtube, Linkedin, Twitter, MessageCircle,
  Globe, Mail, Music2, Chrome, Trash2, Pencil, Eye, EyeOff, Upload, KeyRound,
} from "lucide-react";
import { ADMIN_SECTION_CLIENT_ID } from "@/lib/roles";
import { fileToResizedDataUrl } from "@/lib/image";
import { LinkTokens } from "@/components/LinkifiedText";
import { toast } from "sonner";

const PRESETS = [
  { key: "instagram", label: "Instagram", icon: Instagram, color: "#E1306C" },
  { key: "facebook", label: "Facebook", icon: Facebook, color: "#1877F2" },
  { key: "tiktok", label: "TikTok", icon: Music2, color: "#111827" },
  { key: "youtube", label: "YouTube", icon: Youtube, color: "#FF0000" },
  { key: "linkedin", label: "LinkedIn", icon: Linkedin, color: "#0A66C2" },
  { key: "x", label: "X / Twitter", icon: Twitter, color: "#1DA1F2" },
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "#25D366" },
  { key: "google", label: "Google", icon: Chrome, color: "#EA4335" },
  { key: "website", label: "Sito Web", icon: Globe, color: "#6366F1" },
  { key: "email", label: "Email", icon: Mail, color: "#D946EF" },
];

function ServiceGlyph({ service, size = 20 }) {
  if (service.iconUrl) {
    return (
      <img
        src={service.iconUrl}
        alt={service.name}
        className="h-full w-full object-cover rounded-xl"
      />
    );
  }
  const preset = PRESETS.find((p) => p.key === service.iconKey);
  const Icon = preset?.icon || KeyRound;
  return <Icon size={size} className="text-white" />;
}

export default function AmmAnagrafica() {
  const { clients, updateClient } = useData();
  const adminClient = clients.find((c) => c.id === ADMIN_SECTION_CLIENT_ID);
  const services = useMemo(() => adminClient?.services || [], [adminClient]);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showPw, setShowPw] = useState({});
  const customInputRef = useRef(null);

  const emptyForm = { id: null, name: "", iconKey: "instagram", iconUrl: null, username: "", password: "", other: "" };
  const [form, setForm] = useState(emptyForm);

  const openNew = () => { setEditing(null); setForm({ ...emptyForm }); setOpen(true); };
  const openEdit = (s) => { setEditing(s.id); setForm({ ...emptyForm, ...s }); setOpen(true); };

  const pickPreset = (p) => setForm((f) => ({ ...f, iconKey: p.key, iconUrl: null, name: f.name || p.label }));

  const onCustomIcon = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Icona troppo grande (max 3MB)");
    try {
      const url = await fileToResizedDataUrl(file, 200, 0.85);
      setForm((f) => ({ ...f, iconUrl: url, iconKey: "custom" }));
    } catch {
      toast.error("Errore caricamento icona");
    }
  };

  const persist = async (next) => {
    await updateClient(ADMIN_SECTION_CLIENT_ID, { services: next });
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Inserisci un nome servizio");
    let next;
    if (editing) {
      next = services.map((s) => (s.id === editing ? { ...form, id: editing } : s));
    } else {
      next = [...services, { ...form, id: crypto.randomUUID() }];
    }
    await persist(next);
    setOpen(false);
    toast.success("Servizio salvato");
  };

  const remove = async (id) => {
    if (!confirm("Eliminare questo servizio?")) return;
    await persist(services.filter((s) => s.id !== id));
    toast.success("Servizio eliminato");
  };

  return (
    <div className="space-y-6 tp-fade-in">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Interna</div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white">Amministrazione</h1>
      </div>

      <AmmTabs />

      <section className="tp-glass rounded-3xl overflow-hidden">
        <div className="px-5 sm:px-6 py-4 border-b border-white/5 flex items-center gap-3">
          <KeyRound size={15} className="text-fuchsia-400" />
          <h2 className="text-base font-semibold text-white">Anagrafica · Accessi &amp; servizi</h2>
          <span className="ml-auto text-xs text-zinc-500 tabular-nums">{services.length}</span>
          <Button data-testid="add-service-button" onClick={openNew} className="tp-brand-btn h-9 rounded-full px-3">
            <Plus size={14} className="mr-1.5" /> Servizio
          </Button>
        </div>

        <div className="p-4">
          {services.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-zinc-500 italic">
              Nessun servizio salvato. Aggiungi Instagram, Facebook, ecc. con il pulsante "Servizio".
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {services.map((s) => {
                const preset = PRESETS.find((p) => p.key === s.iconKey);
                const bg = s.iconUrl ? "transparent" : (preset?.color || "#a855f7");
                return (
                  <div
                    key={s.id}
                    data-testid={`service-card-${s.id}`}
                    className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex flex-col gap-3"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className="h-11 w-11 rounded-xl flex items-center justify-center shrink-0 overflow-hidden shadow-md"
                        style={{ background: bg }}
                      >
                        <ServiceGlyph service={s} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-white font-semibold truncate">{s.name}</div>
                        {s.username && <div className="text-xs text-zinc-400 truncate">{s.username}</div>}
                      </div>
                      <button onClick={() => openEdit(s)} className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5" data-testid={`service-edit-${s.id}`}>
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => remove(s.id)} className="p-2 rounded-lg text-zinc-400 hover:text-red-400 hover:bg-red-500/10" data-testid={`service-delete-${s.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="space-y-1.5 text-sm">
                      {s.password ? (
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] uppercase tracking-widest text-zinc-500 w-16">Password</span>
                          <span className="font-mono text-zinc-200 flex-1 truncate">
                            {showPw[s.id] ? s.password : "••••••••"}
                          </span>
                          <button onClick={() => setShowPw((p) => ({ ...p, [s.id]: !p[s.id] }))} className="p-1 text-zinc-400 hover:text-white" data-testid={`service-pw-toggle-${s.id}`}>
                            {showPw[s.id] ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                        </div>
                      ) : null}
                      {s.other ? (
                        <div className="flex items-start gap-2">
                          <span className="text-[10px] uppercase tracking-widest text-zinc-500 w-16 pt-0.5">Altro</span>
                          <span className="text-zinc-300 flex-1 break-words whitespace-pre-wrap">
                            <LinkTokens text={s.other} />
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="tp-glass-strong border-white/10 text-white max-w-md" data-testid="service-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-base">
              {editing ? "Modifica servizio" : "Nuovo servizio"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Servizio</div>
              <div className="grid grid-cols-5 gap-2">
                {PRESETS.map((p) => {
                  const Icon = p.icon;
                  const active = form.iconKey === p.key && !form.iconUrl;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => pickPreset(p)}
                      data-testid={`preset-${p.key}`}
                      title={p.label}
                      className={`h-11 rounded-xl flex items-center justify-center transition-all ${active ? "ring-2 ring-fuchsia-400 scale-105" : "opacity-80 hover:opacity-100"}`}
                      style={{ background: p.color }}
                    >
                      <Icon size={18} className="text-white" />
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => customInputRef.current?.click()}
                  data-testid="preset-custom"
                  title="Nuovo (icona personalizzata)"
                  className={`h-11 rounded-xl flex items-center justify-center border border-dashed border-white/20 overflow-hidden ${form.iconUrl ? "ring-2 ring-fuchsia-400" : "text-zinc-400 hover:text-white hover:border-white/40"}`}
                >
                  {form.iconUrl ? (
                    <img src={form.iconUrl} alt="custom" className="h-full w-full object-cover" />
                  ) : (
                    <Upload size={16} />
                  )}
                </button>
                <input ref={customInputRef} type="file" accept="image/*" className="hidden" onChange={onCustomIcon} data-testid="custom-icon-input" />
              </div>
            </div>
            <Input data-testid="service-name-input" placeholder="Nome servizio (es. Instagram @brand)" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="bg-white/5 border-white/10 text-white" />
            <Input data-testid="service-user-input" placeholder="User / email" value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} className="bg-white/5 border-white/10 text-white" />
            <Input data-testid="service-pw-input" placeholder="Password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className="bg-white/5 border-white/10 text-white" />
            <Input data-testid="service-other-input" placeholder="Altro (note, 2FA, link, ecc.)" value={form.other} onChange={(e) => setForm((f) => ({ ...f, other: e.target.value }))} className="bg-white/5 border-white/10 text-white" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-white/15 bg-transparent text-zinc-300">Annulla</Button>
            <Button onClick={save} data-testid="service-save-button" className="tp-brand-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
